import { completeEnrollment, deleteProposal, getEnrollmentPeriod } from 'api/operations/enrollmentService';
import { isEnrollmentPeriodNotEditable } from 'global/checklists-helpers/ChecklistHelper';
import { action, computed, makeObservable, observable } from 'mobx';
import {
  Checklist,
  ChecklistCard,
  ChecklistCompletionStatus,
  ChecklistType,
  EnrollmentCoreData,
  EnrollmentPeriod,
  EnrollmentProposalData,
  EnrollmentStage,
  EnrollmentType,
  FinalBrowsingCriteria,
  InstalledProposal,
  Proposal,
  ProposalChecklistCard,
  RFQChecklist,
  RfqProposalChecklistData,
  QuoteChecklist,
} from 'types/enrollmentService';

export const INITIAL_ENROLLMENT_OBJECT: Readonly<EnrollmentPeriod> = Object.freeze({
  id: '',
  proposals: [],
  groupId: '',
  hasConfirmedProposal: false,
  effectiveDate: '',
  sponsorId: '',
  productLineIds: [],
  stage: EnrollmentStage.NEW_LEAD,
  type: EnrollmentType.NEW_SALE,
  checklist: null,
  rateSegment: 'GROUP',
});

export default class EnrollmentPeriodModule {
  private _enrollmentPeriod: EnrollmentPeriod = INITIAL_ENROLLMENT_OBJECT;
  private _isLoading: boolean = false;

  private get _confirmedProposal(): Proposal | null {
    return this._enrollmentPeriod.proposals.find((proposal) => proposal.confirmed) || null;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get enrollmentCoreData(): EnrollmentCoreData | null {
    /** Initial stub data */
    if (this._enrollmentPeriod.id === '') return null;
    return {
      id: this._enrollmentPeriod.id,
      groupId: this._enrollmentPeriod.groupId,
      effectiveDate: this._enrollmentPeriod.effectiveDate,
      productLineIds: this._enrollmentPeriod.productLineIds,
      stage: this._enrollmentPeriod.stage,
      type: this._enrollmentPeriod.type,
      rateSegment: this._enrollmentPeriod.rateSegment,
      sponsorId: this._enrollmentPeriod.sponsorId,
    };
  }

  public get enrollmentProposalData(): EnrollmentProposalData | null {
    if (this._enrollmentPeriod.id === '') return null;
    return {
      proposals: this._enrollmentPeriod.proposals,
      confirmedProposal: this._confirmedProposal,
    };
  }

  public get enrollmentPeriod(): EnrollmentPeriod {
    return this._enrollmentPeriod;
  }

  public get checklist(): Checklist | null {
    return this._enrollmentPeriod.checklist || null;
  }

  public get postSubmitChecklist(): Checklist | null {
    return this._enrollmentPeriod.postSubmitChecklist || null;
  }

  public get installedProposal(): InstalledProposal | null {
    return this._enrollmentPeriod.installedProposal || null;
  }

  public get isEnrollmentImmutable(): boolean {
    return isEnrollmentPeriodNotEditable(this._enrollmentPeriod.stage);
  }

  private _setEnrollmentPeriod(period: EnrollmentPeriod): void {
    this._enrollmentPeriod = period;
  }

  private _setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public async uploadEnrollmentPeriod(periodId: string): Promise<void> {
    this._setIsLoading(true);
    const period = await getEnrollmentPeriod(periodId);
    this._setEnrollmentPeriod(period);
    this._setIsLoading(false);
  }

  public getStepCompletionStatus(checklistType: ChecklistType, card: ChecklistCard): ChecklistCompletionStatus {
    let checklistInQuestion;

    if (checklistType === 'DEFAULT') {
      checklistInQuestion = this._enrollmentPeriod.checklist;
    } else if (checklistType === 'POST_SUBMIT') {
      checklistInQuestion = this._enrollmentPeriod.postSubmitChecklist;
    }

    if (!checklistInQuestion) {
      throw new Error(
        `No checklist in current enrollment: ${this._enrollmentPeriod.id} . Unable to retrieve a step status.`,
      );
    }
    const option = checklistInQuestion.checklistOptions.find((o) => o.card === card);
    if (!option) {
      throw new Error(
        `No such option ${card} in current enrollment checklist! Enrollment: ${this._enrollmentPeriod.id} ChecklistType: ${checklistType}`,
      );
    }
    return option.completionStatus;
  }

  /**
   * Get proposal step completion status for both RFQ and Quote cards.
   */
  public getProposalStepCompletionStatus(cartId: string, card: ProposalChecklistCard): ChecklistCompletionStatus {
    const proposal = this.getProposalById(cartId);

    if (!proposal.checklist) {
      throw new Error(`No checklist in current proposal: ${proposal.cartId}. Unable to retrieve a step status.`);
    }

    const option = proposal.checklist.checklistOptions.find((o) => o.card === card);
    if (!option) {
      throw new Error(`No such option ${card} in current proposal checklist!`);
    }
    return option.completionStatus;
  }

  public async requestDeleteProposal(cartId: string): Promise<void> {
    this._setIsLoading(true);
    const newEnrollment = await deleteProposal(this._enrollmentPeriod.id, cartId);
    this._setEnrollmentPeriod(newEnrollment);
    this._setIsLoading(false);
  }

  public async requestCompleteEnrollment(submittedByUserName: string, finalBC: FinalBrowsingCriteria): Promise<void> {
    this._setIsLoading(true);
    const newEnrollment = await completeEnrollment(this._enrollmentPeriod.id, submittedByUserName, finalBC);
    this._setEnrollmentPeriod(newEnrollment);
    this._setIsLoading(false);
  }

  public getProposalById(cartId: string): Proposal {
    const proposal = this._enrollmentPeriod.proposals.find((p) => p.cartId === cartId);
    if (!proposal) {
      throw new Error(`No proposal with id: ${cartId}!`);
    }
    return proposal;
  }

  public rfqProposalChecklistData(cartId: string): RfqProposalChecklistData {
    return {
      groupId: this._enrollmentPeriod.groupId,
      parentGroupId: this._enrollmentPeriod.parentGroupId,
      sponsorId: this._enrollmentPeriod.sponsorId,
      checklist: this.getProposalById(cartId).checklist as RFQChecklist,
    };
  }

  public getQuoteProposalChecklistData(cartId: string): QuoteChecklist {
    return this.getProposalById(cartId).checklist as QuoteChecklist;
  }

  constructor() {
    makeObservable<
      EnrollmentPeriodModule,
      '_enrollmentPeriod' | '_isLoading' | '_setEnrollmentPeriod' | '_setIsLoading' | '_confirmedProposal'
    >(this, {
      _enrollmentPeriod: observable,
      _isLoading: observable,

      _confirmedProposal: computed,
      isLoading: computed,
      enrollmentCoreData: computed,
      enrollmentProposalData: computed,
      checklist: computed,
      postSubmitChecklist: computed,
      installedProposal: computed,

      _setEnrollmentPeriod: action.bound,
      _setIsLoading: action.bound,
      uploadEnrollmentPeriod: action.bound,
      getStepCompletionStatus: action.bound,
      getProposalStepCompletionStatus: action.bound,
      requestDeleteProposal: action.bound,
      requestCompleteEnrollment: action.bound,
      getProposalById: action.bound,
      rfqProposalChecklistData: action.bound,
      getQuoteProposalChecklistData: action.bound,
    });
  }
}
